import { Box, Chip, SxProps, Theme, Tooltip } from "@mui/material";
import React from "react";
import {
  formatDateForDisplay,
  isDateOlderThanMonths,
} from "../../utils/dateUtils";
import { projectStatusMapping } from "../../utils/projectStatusMapping";

interface ProjectStatusChipProps {
  statusId: string;
  lastUpdatedDate?: string | Date | null;
  size?: "small" | "medium";
  sx?: SxProps<Theme>;
}

/**
 * Reusable project status chip component with stale data indicator
 * Shows a warning icon (⚠️) when the last update is older than 6 months
 */
const ProjectStatusChip: React.FC<ProjectStatusChipProps> = ({
  statusId,
  lastUpdatedDate,
  size = "small",
  sx,
}) => {
  // Get display name and color from mapping utility
  const displayName = projectStatusMapping.getDisplayName(statusId);
  const color = projectStatusMapping.getStatusColor(statusId);

  // Define final statuses that should not show stale data warning
  const finalStatuses = ["completed", "cancelled", "failed"];
  const isFinalStatus = finalStatuses.includes(statusId.toLowerCase());

  // Check if data is stale (older than 6 months) and not a final status
  const isStale = !isFinalStatus && isDateOlderThanMonths(lastUpdatedDate, 6);

  // Create chip label with optional warning icon
  const chipLabel = isStale ? (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <span>{displayName}</span>
      {/* <span style={{ fontSize: "14px", opacity: 1, filter: "grayscale(100%)" }}> */}
      ⓘ{/* </span> */}
    </Box>
  ) : (
    displayName
  );

  // Create tooltip content for stale data
  const tooltipContent = isStale ? (
    <Box>
      <Box>Last updated: {formatDateForDisplay(lastUpdatedDate)}</Box>
      <Box>
        We did not find any recent information on this project, so the status
        may be outdated.
      </Box>
    </Box>
  ) : (
    ""
  );

  // Render chip with conditional tooltip
  const chip = <Chip label={chipLabel} size={size} color={color} sx={sx} />;

  // Wrap in tooltip only if data is stale
  return isStale ? (
    <Tooltip title={tooltipContent} arrow>
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};

export default ProjectStatusChip;
